import { LoadingRegion } from "./loading-region";
import { SkeletonLine, SkeletonBlock, SkeletonAvatarSm } from "./skeleton-primitives";
import { cn } from "@/lib/utils";

/** Default centered panel used by several Studio Data tabs on first fetch. */
export function StudioDataPanelSkeleton({ className }: { className?: string }) {
  return (
    <LoadingRegion busy labelKey="common.loadingContent" className={cn("flex flex-col items-center justify-center py-16", className)}>
      <div className="w-full max-w-3xl space-y-4 px-4">
        <SkeletonLine className="mx-auto h-5 w-48" />
        <SkeletonBlock className="h-32 w-full rounded-2xl" />
        <div className="space-y-2">
          <SkeletonLine className="h-4 w-full" />
          <SkeletonLine className="h-4 w-full" />
          <SkeletonLine className="h-4 w-4/5" />
        </div>
        <SkeletonBlock className="h-24 w-full rounded-xl" />
      </div>
    </LoadingRegion>
  );
}

/** Users / team style rows */
export function StudioDataUsersSkeleton({ className }: { className?: string }) {
  return (
    <LoadingRegion busy labelKey="common.loadingContent" className={cn("space-y-3 py-4", className)}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 rounded-xl border border-border/40 p-3">
          <SkeletonAvatarSm />
          <div className="min-w-0 flex-1 space-y-2">
            <SkeletonLine className="h-4 w-40" />
            <SkeletonLine className="h-3 w-56" />
          </div>
          <SkeletonBlock className="h-8 w-20 shrink-0 rounded-lg" />
        </div>
      ))}
    </LoadingRegion>
  );
}

/** FAQ / policies list of cards */
export function StudioDataCardListSkeleton({ className }: { className?: string }) {
  return (
    <LoadingRegion busy labelKey="common.loadingContent" className={cn("space-y-4 py-4", className)}>
      {Array.from({ length: 4 }).map((_, i) => (
        <SkeletonBlock key={i} className="min-h-[100px] w-full rounded-xl border border-border/40 p-4">
          <SkeletonLine className="mb-3 h-4 w-48" />
          <SkeletonLine className="h-3 w-full" />
          <SkeletonLine className="mt-2 h-3 max-w-[85%]" />
        </SkeletonBlock>
      ))}
    </LoadingRegion>
  );
}

function ConnectionsTableRowSkeleton() {
  return (
    <tr className="border-b border-border/30 last:border-0">
      <td className="px-5 py-3.5">
        <div className="flex items-center gap-2.5">
          <SkeletonAvatarSm className="size-8" />
          <SkeletonLine className="h-4 w-24" />
        </div>
      </td>
      <td className="px-5 py-3.5">
        <SkeletonLine className="h-4 w-32" />
      </td>
      <td className="px-5 py-3.5">
        <SkeletonLine className="h-5 w-24 rounded-full" />
      </td>
      <td className="px-5 py-3.5">
        <SkeletonLine className="h-5 w-20 rounded-full" />
      </td>
      <td className="px-5 py-3.5 text-right">
        <div className="ml-auto flex items-center justify-end gap-2">
          <SkeletonLine className="h-4 w-20" />
          <SkeletonBlock className="size-8 rounded-lg" />
        </div>
      </td>
    </tr>
  );
}

/** Settings → Channels table (channel, identifier, applies to, status, actions). */
export function ConnectionsTableSkeleton({ className }: { className?: string }) {
  return (
    <div data-testid="connections-table-skeleton" className={cn("space-y-4", className)}>
      <LoadingRegion busy labelKey="common.loadingTable" className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <SkeletonBlock className="h-9 w-36 rounded-lg" />
          <SkeletonBlock className="h-9 w-44 rounded-lg" />
        </div>
        <SkeletonBlock className="h-24 w-full rounded-xl border border-border/40" />
        <div className="overflow-hidden rounded-2xl border border-border/50 bg-card">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border/50 bg-muted/30">
                {Array.from({ length: 5 }).map((_, i) => (
                  <th key={i} className="px-5 py-3 text-left">
                    <SkeletonLine className="h-3 w-16" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 3 }).map((_, i) => (
                <ConnectionsTableRowSkeleton key={i} />
              ))}
            </tbody>
          </table>
        </div>
      </LoadingRegion>
    </div>
  );
}

/** @deprecated Use {@link ConnectionsTableSkeleton}. */
export const StudioDataConnectionsSkeleton = ConnectionsTableSkeleton;

/** Classes schedule toolbar + grid hint */
export function StudioDataClassesSkeleton({ className }: { className?: string }) {
  return (
    <LoadingRegion busy labelKey="common.loadingContent" className={cn("space-y-4 py-4", className)}>
      <div className="flex flex-wrap items-center gap-2">
        <SkeletonBlock className="h-9 w-32 rounded-lg" />
        <SkeletonBlock className="h-9 w-40 rounded-lg" />
        <div className="flex-1" />
        <SkeletonBlock className="h-9 w-28 rounded-lg" />
      </div>
      <SkeletonBlock className="min-h-[280px] w-full rounded-2xl border border-border/40" />
    </LoadingRegion>
  );
}

/** Agent trace list + detail */
/** Agent trace run list (empty list + loading). */
export function TraceRunsListSkeleton() {
  return (
    <LoadingRegion busy labelKey="studioData.tabs.trace.loadingRuns" className="px-2 py-4">
      <table className="w-full text-left">
        <tbody>
          {Array.from({ length: 6 }).map((_, i) => (
            <tr key={i} className="border-b border-border/30">
              <td className="px-5 py-3">
                <SkeletonLine className="h-4 w-36" />
                <SkeletonLine className="mt-1 h-3 w-48" />
              </td>
              <td className="px-5 py-3">
                <SkeletonLine className="h-4 w-16" />
              </td>
              <td className="px-5 py-3">
                <SkeletonLine className="h-4 w-44" />
              </td>
              <td className="px-5 py-3 text-right">
                <SkeletonLine className="ml-auto h-4 w-8" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </LoadingRegion>
  );
}

export function AgentTraceSkeleton({ className }: { className?: string }) {
  return (
    <LoadingRegion busy labelKey="common.loadingContent" className={cn("flex min-h-[320px] gap-4 py-4", className)}>
      <div className="w-full max-w-xs shrink-0 space-y-2 border-r border-border/40 pr-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonBlock key={i} className="h-12 w-full rounded-lg" />
        ))}
      </div>
      <div className="min-w-0 flex-1 space-y-3">
        <SkeletonLine className="h-5 w-48" />
        <SkeletonLine className="h-3 w-full" />
        <SkeletonLine className="h-3 w-full" />
        <SkeletonBlock className="h-40 w-full rounded-xl" />
      </div>
    </LoadingRegion>
  );
}
